/**
 * Test Ably Connection Utility
 *
 * This utility helps test and debug Ably notification connections
 */

export interface AblyTestResult {
  success: boolean;
  message: string;
  details?: any;
  timestamp: Date;
}

export class AblyConnectionTester {
  private apiKey: string;
  private channel: string;

  constructor(apiKey: string, channel: string = 'notifications') {
    this.apiKey = apiKey;
    this.channel = channel;
  }

  /**
   * Test Ably connection and publish a test message
   */
  async testConnection(): Promise<AblyTestResult> {
    try {
      console.log('🧪 Testing Ably connection...');

      // Dynamic import to avoid build issues
      const Ably = await import('ably');

      // Create Ably client
      const ably = new Ably.Realtime({
        key: this.apiKey,
        clientId: `test-client-${Date.now()}`,
        echoMessages: false,
      });

      // Set up connection promise
      const connectionPromise = new Promise<void>((resolve, reject) => {
        ably.connection.on('connected', () => {
          console.log('✅ Ably test connection successful');
          resolve();
        });

        ably.connection.on('failed', (error) => {
          console.error('❌ Ably test connection failed:', error);
          reject(error);
        });

        // Timeout after 10 seconds
        setTimeout(() => {
          reject(new Error('Connection timeout'));
        }, 10000);
      });

      // Wait for connection
      await connectionPromise;

      // Test publishing a message
      const channel = ably.channels.get(this.channel);
      const testMessage = {
        id: `test-${Date.now()}`,
        title: 'Test Notification',
        message: 'This is a test message from Ably connection tester',
        type: 'info',
        timestamp: new Date().toISOString(),
        data: { test: true },
      };

      await channel.publish('notification', testMessage);
      console.log('✅ Test message published successfully');

      // Close connection
      ably.close();

      return {
        success: true,
        message: 'Ably connection test successful',
        details: {
          apiKey: this.apiKey.substring(0, 10) + '...',
          channel: this.channel,
          testMessage,
        },
        timestamp: new Date(),
      };
    } catch (error) {
      console.error('❌ Ably connection test failed:', error);

      return {
        success: false,
        message: `Ably connection test failed: ${
          error instanceof Error ? error.message : String(error)
        }`,
        details: {
          apiKey: this.apiKey.substring(0, 10) + '...',
          channel: this.channel,
          error: error instanceof Error ? error.message : String(error),
        },
        timestamp: new Date(),
      };
    }
  }

  /**
   * Test receiving messages from Ably
   */
  async testReceiving(): Promise<AblyTestResult> {
    try {
      console.log('🧪 Testing Ably message receiving...');

      const Ably = await import('ably');

      const ably = new Ably.Realtime({
        key: this.apiKey,
        clientId: `receiver-${Date.now()}`,
        echoMessages: false,
      });

      // Set up connection promise
      const connectionPromise = new Promise<void>((resolve, reject) => {
        ably.connection.on('connected', () => {
          console.log('✅ Ably receiver connected');
          resolve();
        });

        ably.connection.on('failed', (error) => {
          console.error('❌ Ably receiver connection failed:', error);
          reject(error);
        });

        setTimeout(() => {
          reject(new Error('Receiver connection timeout'));
        }, 10000);
      });

      await connectionPromise;

      // Set up message listener
      const channel = ably.channels.get(this.channel);
      const messagePromise = new Promise<any>((resolve) => {
        const timeout = setTimeout(() => {
          resolve(null); // No message received
        }, 5000);

        channel.subscribe('notification', (message) => {
          clearTimeout(timeout);
          resolve(message);
        });
      });

      // Wait for a message (or timeout)
      const receivedMessage = await messagePromise;

      ably.close();

      if (receivedMessage) {
        return {
          success: true,
          message: 'Ably message receiving test successful',
          details: {
            receivedMessage: receivedMessage.data,
            channel: this.channel,
          },
          timestamp: new Date(),
        };
      } else {
        return {
          success: false,
          message: 'No message received within timeout period',
          details: {
            channel: this.channel,
            timeout: '5 seconds',
          },
          timestamp: new Date(),
        };
      }
    } catch (error) {
      console.error('❌ Ably receiving test failed:', error);

      return {
        success: false,
        message: `Ably receiving test failed: ${
          error instanceof Error ? error.message : String(error)
        }`,
        details: {
          error: error instanceof Error ? error.message : String(error),
        },
        timestamp: new Date(),
      };
    }
  }

  /**
   * Run comprehensive Ably tests
   */
  async runFullTest(): Promise<{
    connection: AblyTestResult;
    receiving: AblyTestResult;
    overall: boolean;
  }> {
    console.log('🚀 Running comprehensive Ably tests...');

    const connectionTest = await this.testConnection();
    const receivingTest = await this.testReceiving();

    const overall = connectionTest.success && receivingTest.success;

    console.log('📊 Test Results:');
    console.log(`  Connection: ${connectionTest.success ? '✅' : '❌'}`);
    console.log(`  Receiving: ${receivingTest.success ? '✅' : '❌'}`);
    console.log(`  Overall: ${overall ? '✅' : '❌'}`);

    return {
      connection: connectionTest,
      receiving: receivingTest,
      overall,
    };
  }
}

/**
 * Quick test function for browser console
 */
export async function testAblyInBrowser(): Promise<void> {
  const apiKey = import.meta.env.VITE_POS_DASHBOARD_ABLY_API_KEY;
  const channel =
    import.meta.env.VITE_POS_DASHBOARD_ABLY_CHANNEL || 'notifications';

  if (!apiKey) {
    console.error('❌ VITE_POS_DASHBOARD_ABLY_API_KEY not found');
    console.log('💡 Set your Ably API key in environment variables');
    return;
  }

  console.log('🧪 Testing Ably connection in browser...');
  console.log(`API Key: ${apiKey.substring(0, 10)}...`);
  console.log(`Channel: ${channel}`);

  const tester = new AblyConnectionTester(apiKey, channel);
  const results = await tester.runFullTest();

  console.log('📊 Test Results:', results);

  if (results.overall) {
    console.log('🎉 Ably is working correctly!');
  } else {
    console.log('❌ Ably has issues. Check the details above.');
  }
}

// Make it available globally for browser testing
if (typeof window !== 'undefined') {
  (window as any).testAblyInBrowser = testAblyInBrowser;
  (window as any).AblyConnectionTester = AblyConnectionTester;
}
